import { createContext, useContext, useCallback, useRef, useEffect, useState, type ReactNode } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface UnsavedChangesContextValue {
  register: (id: string) => void;
  unregister: (id: string) => void;
}

const UnsavedChangesContext = createContext<UnsavedChangesContextValue | null>(null);

type PendingNavigation =
  | { type: "pushState"; args: [unknown, string, string | URL | null | undefined]; method: "pushState" | "replaceState" }
  | { type: "popstate"; previousUrl: string };

export function UnsavedChangesProvider({ children }: { children: ReactNode }) {
  const dirtyPagesRef = useRef(new Set<string>());
  const isNavigatingRef = useRef(false);
  const currentUrlRef = useRef(window.location.href);

  const [pendingNav, setPendingNav] = useState<PendingNavigation | null>(null);

  const hasDirtyPages = useCallback(() => dirtyPagesRef.current.size > 0, []);

  const register = useCallback((id: string) => {
    dirtyPagesRef.current.add(id);
  }, []);

  const unregister = useCallback((id: string) => {
    dirtyPagesRef.current.delete(id);
  }, []);

  useEffect(() => {
    const originalPushState = history.pushState.bind(history);
    const originalReplaceState = history.replaceState.bind(history);

    const guardedMethod = (original: typeof history.pushState, methodName: "pushState" | "replaceState") => {
      return function (this: History, ...args: Parameters<typeof history.pushState>) {
        if (isNavigatingRef.current || !hasDirtyPages()) {
          const result = original.apply(this, args);
          currentUrlRef.current = window.location.href;
          return result;
        }
        // Block the navigation and show a React dialog instead of window.confirm
        setPendingNav({ type: "pushState", args: args as [unknown, string, string | URL | null | undefined], method: methodName });
      };
    };

    history.pushState = guardedMethod(originalPushState, "pushState");
    history.replaceState = guardedMethod(originalReplaceState, "replaceState");

    // For browser back/forward (popstate): let the navigation happen,
    // then show a dialog offering to go back. This avoids window.confirm()
    // which browsers can silently suppress in popstate handlers.
    const handlePopState = () => {
      if (isNavigatingRef.current || !hasDirtyPages()) {
        currentUrlRef.current = window.location.href;
        return;
      }

      const previousUrl = currentUrlRef.current;
      currentUrlRef.current = window.location.href;

      // Show dialog after React re-renders for the new page
      setTimeout(() => {
        setPendingNav({ type: "popstate", previousUrl });
      }, 0);
    };

    window.addEventListener("popstate", handlePopState, true);

    return () => {
      history.pushState = originalPushState;
      history.replaceState = originalReplaceState;
      window.removeEventListener("popstate", handlePopState, true);
    };
  }, [hasDirtyPages]);

  const handleConfirmLeave = useCallback(() => {
    if (!pendingNav) return;

    if (pendingNav.type === "pushState") {
      // Replay the blocked pushState/replaceState
      isNavigatingRef.current = true;
      const method = pendingNav.method === "replaceState" ? history.replaceState : history.pushState;
      method.apply(history, pendingNav.args);
      isNavigatingRef.current = false;
      currentUrlRef.current = window.location.href;
    }
    // For popstate, navigation already happened — just close the dialog

    setPendingNav(null);
  }, [pendingNav]);

  const handleCancelLeave = useCallback(() => {
    if (!pendingNav) return;

    if (pendingNav.type === "popstate") {
      // Navigate back to the page the user was on
      isNavigatingRef.current = true;
      history.pushState(null, "", pendingNav.previousUrl);
      isNavigatingRef.current = false;
      currentUrlRef.current = pendingNav.previousUrl;
    }
    // For pushState, we already blocked it — nothing to undo

    setPendingNav(null);
  }, [pendingNav]);

  return (
    <UnsavedChangesContext.Provider value={{ register, unregister }}>
      {children}
      <AlertDialog open={!!pendingNav} onOpenChange={(open) => { if (!open) handleCancelLeave(); }}>
        <AlertDialogContent data-testid="unsaved-changes-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Niezapisane zmiany</AlertDialogTitle>
            <AlertDialogDescription>
              Masz niezapisane zmiany. Czy na pewno chcesz opuścić stronę?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancelLeave} data-testid="unsaved-changes-stay">
              Pozostań na stronie
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmLeave} data-testid="unsaved-changes-leave">
              Opuść stronę
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </UnsavedChangesContext.Provider>
  );
}

export function useUnsavedChangesContext() {
  return useContext(UnsavedChangesContext);
}
